<template>
  <div>
    <b-modal
      id="generation-modal"
      :visible="isGenerating"
      hide-footer
      hide-header
      centered
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="text-center">
        <h5>Landing generation...</h5>
        <b-progress
          :value="progress"
          max="100"
          height="20px"
          class="mt-3"
        >
          <b-progress-bar
            :value="progress"
            :label="`${progress}%`"
            animated
            striped
            variant="primary"
          />
        </b-progress>
      </div>
    </b-modal>
    <b-card>
      <div class="head-card mb-2">
        <div class="head-card-top">
          <h4>{{ $t('Main settings') }}</h4>
          <feather-icon
            v-b-tooltip.hover.top="$t('Close')"
            icon="XIcon"
            size="18"
            class="toggle-close cursor-pointer"
            @click="$router.go(-1)"
          />
        </div>
        <hr>
        <validation-observer ref="validationLandingRules">
          <h5>{{ $t('Title') }}</h5>
          <validation-provider
            #default="{ errors }"
            :name="$t('Title')"
            rules="required"
          >
            <b-form-input
              v-model="landing.title"
              :placeholder="$t('Title')"
              :state="errors.length > 0 ? false:null"
              @blur="handleTitleBlur"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </validation-observer>
      </div>

      <h5>{{ $t('landings.form') }}</h5>
      <b-row class="mb-1">
        <b-col
          cols="12"
          md="6"
        >
          <div v-if="!formBuild.length">
            <div class="alert alert-warning">
              <div class="alert-body">
                No form fields
              </div>
            </div>
          </div>
          <div
            v-for="(formField, idx) in formBuild"
            :key="`${formField.value}-${idx}`"
          >
            <b-row class="mb-1">
              <b-col cols="1">
                {{ idx + 1 }}
              </b-col>
              <b-col cols="4">
                <b-form-select
                  v-model="formField.value"
                  :options="formTypes"
                />
              </b-col>
              <b-col cols="5">
                <template v-if="formField.value === 'select'">
                  <b-form-input
                    :id="`${formField.value}-${idx}`"
                    v-model="formField.text"
                    placeholder="Label"
                    class="mb-1"
                  />
                  <b-form-input
                    :id="`${formField.value}-${idx}`"
                    v-model="formField.options"
                    placeholder="value1, value2, value3"
                  />
                </template>
                <template v-else>
                  <b-form-input
                    :id="`${formField.value}-${idx}`"
                    v-model="formField.text"
                    placeholder="Label"
                  />
                </template>
              </b-col>
              <b-col cols="2">
                <b-button
                  variant="danger"
                  size="sm"
                  @click="deleteField(formBuild, idx)"
                >
                  <feather-icon
                    v-b-tooltip.hover.top="$t('form.delete')"
                    icon="XIcon"
                    size="18"
                  />
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-button
            variant="primary"
            @click="addField(formBuild)"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-25"
            />
            <span>Add Field</span>
          </b-button>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <div class="mt-2">
            <b-form-checkbox v-model="isAdditionMessage">
              Enable additional question?
            </b-form-checkbox>
          </div>

          <b-form-input
            v-if="isAdditionMessage"
            v-model="additionalMessage"
            placeholder="Enter the message after form submission"
            class="mt-2 mb-2"
          />

          <b-form-group
            label="Success form send message"
            class="mt-2"
          >
            <b-form-input
              v-model="successSendMessage"
              placeholder="Your form has been successfully"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="head-card mt-2">
        <div class="head-card-top">
          <h5>{{ $t('landings.describe') }}</h5>
        </div>
        <b-form-textarea
          v-model="userDescription"
          :placeholder="$t('landings.describe_placeholder')"
          rows="3"
        />
        <p
          v-if="formBuild.length === 0"
          class="text-danger mt-2 mb-1"
        >
          {{ $t('landings.landing_needs_form') }}
        </p>
        <b-button
          class="mt-2"
          variant="success"
          :disabled="isLoading || isGenerateDisabled || userDescription === ''"
          @click="generateLandingPage"
        >
          <feather-icon
            icon="CpuIcon"
            class="mr-25"
          />
          {{ isLoading ? 'Generating...' : $t('landings.generate') }}
        </b-button>
      </div>
    </b-card>
    <b-card
      v-if="generatedUrl"
      class="mt-2"
    >
      <h4>{{ $t('landings.generated') }}</h4>

      <div
        class="mt-2"
      >
        <iframe
          ref="generatedIframe"
          :src="generatedUrl"
          width="100%"
          height="600"
          style="border:1px solid #ccc;"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { getUserData } from '@/auth/utils'
import options from '@/libs/options'
import Ripple from 'vue-ripple-directive'
import {
  required,
} from '@validations'
import {
  ValidationProvider,
  ValidationObserver,
} from 'vee-validate'

import {
  BCard,
  VBTooltip,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormTextarea,
  BFormSelect,
  BButton,
  BRow,
  BCol,
  BModal,
  BProgress,
  BProgressBar,
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormTextarea,
    BFormSelect,
    BButton,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BProgress,
    BProgressBar,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: {
    landing: {
      type: Object,
      require: true,
      default: () => {},
    },
    type: {
      type: String,
      default: 'edit',
    },
  },
  data() {
    return {
      required,
      generatedUrl: '',
      isLoading: false,
      authUserData: getUserData(),
      breadcrumbItems: [],
      account: {},
      generatedData: {},
      userTitle: '',
      userDescription: '',
      formTypes: [
        { value: 'text', text: 'Text' },
        { value: 'tel', text: 'Telephone' },
        { value: 'email', text: 'E-mail' },
        { value: 'select', text: 'Select' },
        { value: 'textarea', text: 'Textarea' },
      ],
      formBuild: [],
      isAdditionMessage: false,
      additionalMessage: '',
      showConfirmation: false,
      disabledBtn: false,
      successSendMessage: 'Your form has been successfully submitted. Thank you!',
      ...options,
      isGenerating: false,
      progress: 0,
      progressTimer: null,
    }
  },
  computed: {
    statusesLocale() {
      return this.statuses.map(s => ({
        value: s.value,
        text: this.$t(`selects.statuses.${s.value}`),
      }))
    },
    isGenerateDisabled() {
      return this.isLoading || this.formBuild.length === 0
    },
  },
  mounted() {
    this.initForm()
  },
  created() {
    this.getUserData()
  },
  methods: {
    initForm() {
      const fields = this.landing?.data?.form_config?.fields
      if (fields) {
        this.formBuild = fields.map(f => ({
          value: f.type,
          text: f.label,
          ...(f.options && { options: Array.isArray(f.options) ? f.options.join(', ') : f.options }),
        }))
      }

      this.isAdditionMessage = this.landing.data?.isAdditionMessage || false
      this.additionalMessage = this.landing.data?.additionalMessage || ''
      if (this.landing.data?.form_config?.success_message) {
        this.successSendMessage = this.landing.data.form_config.success_message
      }
      this.userDescription = this.landing.data?.promt || ''
    },
    async handleTitleBlur() {
      if (this.type === 'edit') {
        try {
          await this.saveForm()
        } catch (error) {
          this.$store.dispatch(
            'error-store/showNotify',
            {
              title: 'Error when auto-saving the title',
              msg: error.detail,
              variant: 'danger',
            },
            { root: true },
          )
        }
      }
    },
    async saveFields() {
      // const formFields = this.formBuild.map(f => ({ type: f.value, label: f.text }))
      const formFields = this.formBuild.map(f => ({
        type: f.value,
        label: f.text,
        ...(f.options && { options: f.options.split(',').map(v => v.trim()) }),
      }))

      this.landing.data = {
        form_config: {
          fields: formFields,
          submit_url: 'https://api.example.com/contact',
          success_message: this.successSendMessage,
        },
      }
    },
    async generateLandingPage() {
      this.isGenerating = true
      this.progress = 0
      this.progressTimer = setInterval(() => {
        if (this.progress < 90) {
          const increment = Math.max(1, 5 - Math.floor(this.progress / 20))
          this.progress += increment
        }
      }, 900)

      try {
        await this.saveForm()
        this.isLoading = true
        const params = {
          title: this.landing.title,
          description: this.userDescription,
          form_config: this.landing.data.form_config,
        }
        const landingId = this.landing._id

        const responseData = await useJwt.generateLanding(this.$route.params.userId, landingId, params)
        this.generatedUrl = responseData.data.url

        clearInterval(this.progressTimer)
        this.progress = 100

        setTimeout(() => {
          this.isGenerating = false
        }, 1000)

        this.$nextTick(() => {
          setTimeout(() => {
            const iframe = this.$refs.generatedIframe
            if (iframe && typeof iframe.scrollIntoView === 'function') {
              iframe.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 500)
        })
      } catch (err) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Landing page generation error!',
            msg: err.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
        clearInterval(this.progressTimer)

        setTimeout(() => {
          this.isGenerating = false
        }, 500)
      }
    },
    async getUserData() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || []
    },
    addField(fields) {
      this.$refs.validationLandingRules.validate().then(success => {
        if (!success) return
        fields.push({
          value: 'text',
          text: '',
        })
        this.generatedData = {}
      })
    },
    deleteField(fields, idx) {
      this.$refs.validationLandingRules.validate().then(success => {
        if (!success) return

        if (idx >= 0 && idx < fields.length) {
          fields.splice(idx, 1)
        }
        this.generatedData = {}
      })
    },
    async saveForm() {
      const success = await this.$refs.validationLandingRules.validate()
      if (!success) return

      this.saveFields()
      this.disabledBtn = true

      this.landing.data.isAdditionMessage = this.isAdditionMessage
      this.landing.data.additionalMessage = this.additionalMessage
      this.landing.data.promt = this.userDescription

      if (this.type === 'create') {
        await useJwt.createLandingPage({ landingpage: this.landing })
          .then(response => {
            this.$emit('switch-to-edit')
            this.landing._id = response.data.landingpage._id
            this.$store.dispatch('error-store/showNotify', {
              title: this.$t('landings.created'),
              icon: 'EditIcon',
              variant: 'success',
            }, { root: true })
          }).catch(error => {
            if (error.response && error.response.status === 422) {
              if (error.response.data.errors.length) {
                error.response.data.errors.forEach(err => {
                  const field = err.source.pointer.split('/')
                  this.$store.dispatch(
                    'error-store/showNotify',
                    {
                      title: `Error [${field[field.length - 1]}]`,
                      msg: err.detail,
                      variant: 'danger',
                    },
                    { root: true },
                  )
                  this.$refs.validationWidgetRules.setErrors({ [field[field.length - 1]]: err.detail })
                })
              }
            }
            if (error.response && error.response.status === 500) {
              this.$store.dispatch(
                'error-store/showNotify',
                {
                  title: error.response.statusText,
                  msg: error.message,
                  variant: 'danger',
                },
                { root: true },
              )
            }
          })
          .finally(() => {
            this.disabledBtn = false
          })
      } else {
        await useJwt.updateLandingPage(this.landing._id, { landingpage: this.landing })
          .then(() => {
            this.$store.dispatch('error-store/showNotify', {
              title: this.$t('landings.updated'),
              icon: 'EditIcon',
              variant: 'success',
            }, { root: true })
          })
          .catch(error => {
            if (error.response && error.response.status === 422) {
              if (error.response.data.errors.length) {
                error.response.data.errors.forEach(err => {
                  this.$store.dispatch(
                    'error-store/showNotify',
                    {
                      title: 'Error',
                      msg: err.detail,
                      variant: 'danger',
                    },
                    { root: true },
                  )
                })
              }
            }
          })
          .finally(() => {
            this.disabledBtn = false
          })
      }
    },
  },
}
</script>

<style scoped>
.landing-preview {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;
  min-height: 150px;
}
</style>
